import { useState } from 'react';
import styled from '@emotion/styled';
import { Spacings, mediaQuery, Fonts } from '@styles/index';
import MUIDialog from '@material-ui/core/Dialog';
import Typography from 'aquilla/core/atoms/Typography';
import YouTube from '@cont/FreeLesson/externalyoutube';
import Image from '@base/FutureImage';
import useWindowSize from '@hooks/useWindowSize';
import pushRoute from '@utils/pushRoute';
import HeadingMetrics from './HeadingMetrics';
import GoalBreadcrumb from './GoalBreadcrumb';
import AllCentreDialogEnquiry from './EnquiryFormDialog/AllCentreDialogEnquiry';

const Container = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const Wrapper = styled.div`
  width: 1136px;
  display: flex;
  flex-direction: row;
  gap: ${Spacings.SPACING_10B};
  ${mediaQuery.uptoMobile} {
    width: 100%;
    padding: ${Spacings.SPACING_4B};
  }
  ${mediaQuery.uptoTablet} {
    width: 100%;
    padding: ${Spacings.SPACING_4B};
  }
`;

const LeftContainer = styled.div`
  width: 100%;
  gap: ${Spacings.SPACING_7B};
  display: flex;
  flex-direction: column;
  ${mediaQuery.uptoMobile} {
    gap: ${Spacings.SPACING_4B};
  }
`;

const TitleContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_3B};
`;

const BreadcrumbContainer = styled.div`
  width: fit-content;
  height: 24px;
  display: flex;
  position: relative;
  align-items: center;
  border-radius: ${Spacings.SPACING_4B};
`;

const BreadcrumbBgWrapper = styled.div`
  display: flex;
  position: absolute;
  width: 100%;
  height: 100%;
`;

const BreadcrumbTextContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${Spacings.SPACING_2};
  align-items: center;
  z-index: 1;
  padding: ${Spacings.SPACING_1B} ${Spacings.SPACING_13};
`;

const BreadcrumbTextWrapper = styled.div`
  color: var(--color-i-brown-2);
  font-size: ${Fonts.H7};
  font-weight: ${Fonts.HEAVY};
  line-height: ${Spacings.SPACING_4B};
`;

const MetricsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: ${Spacings.SPACING_8B};
  ${mediaQuery.uptoMobile} {
    width: 100%;
    display: grid;
    grid-template-columns: auto auto;
    gap: ${Spacings.SPACING_4B} ${Spacings.SPACING_8B};
  }
`;

const ButtonsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: ${Spacings.SPACING_4B};
  ${mediaQuery.uptoMobile} {
    width: 100%;
    flex-direction: column;
  }
`;

const LocationButtonContainer = styled.div`
  width: fit-content;
  height: ${Spacings.SPACING_12B};
  display: flex;
  flex-direction: row;
  border-radius: ${Spacings.SPACING_2B};
  align-items: center;
  justify-content: center;
  gap: ${Spacings.SPACING_2B};
  cursor: pointer;
  background-color: var(--color-i-black);
  padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_8B} ${Spacings.SPACING_4B}
    ${Spacings.SPACING_8B};
  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const LocationButtonTextWrapper = styled.span`
  color: var(--color-i-white);
  font-size: ${Fonts.H6};
  font-weight: ${Fonts.HEAVY};
`;

const FormButtonContainer = styled.div`
  width: fit-content;
  height: ${Spacings.SPACING_12B};
  display: flex;
  flex-direction: row;
  border-width: 1px;
  border-style: solid;
  border-color: var(--color-i-gray-97);
  border-radius: ${Spacings.SPACING_2B};
  align-items: center;
  justify-content: center;
  gap: ${Spacings.SPACING_2B};
  cursor: pointer;
  background-color: var(--color-i-white-99);
  padding-right: ${Spacings.SPACING_8B};
  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
  :hover {
    background-color: var(--color-i-gray-96);
    border-color: var(--color-i-gray-95);
  }
  :active {
    background-color: var(--color-i-gray-94);
    border-color: var(--color-i-gray-93);
  }
`;

const FormButtonTextWrapper = styled.span`
  color: var(--color-i-black);
  font-size: ${Fonts.H6};
  font-weight: ${Fonts.HEAVY};
`;

const RightContainer = styled.div`
  min-width: 411px;
  ${mediaQuery.uptoMobile} {
    display: none;
  }
  ${mediaQuery.uptoTablet} {
    display: none;
  }
`;

const VideoCardContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: ${Spacings.SPACING_3B};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledImg = styled(Image)`
  width: 100%;
  height: 100%;
  border-radius: ${Spacings.SPACING_3B};
`;

const PlayBtn = styled.img`
  position: absolute;
  cursor: pointer;
`;

const StyledDialog = styled(MUIDialog)`
  &.MuiDialog-root .MuiDialog-container .MuiPaper-root {
    position: absolute;
    background: var(--color-text-primary);
    height: 80%;
    width: 88%;
    max-width: 88%;
    @media only screen and (max-width: 540px) {
      margin: 16px auto;
      height: 30%;
    }
  }
`;

const EnquiryFormStyledDialog = styled(MUIDialog)`
  margin: 0px;
  .MuiDialog-paperWidthSm {
    max-width: 800px;
  }
  .MuiDialog-paperScrollPaper {
    max-height: 510px;
  }
  &.MuiDialog-root .MuiDialog-container .MuiPaper-root {
    margin: 0px;
    position: absolute;
    min-width: 800px;
    min-height: 510px;
  }
`;

const opts = {
  height: '100%',
  width: '100%',
  playerVars: {
    autoplay: 1,
    showinfo: 0,
    rel: 0
  }
};

const AllCentreHeading = ({
  goalBreadcrumbData,
  headingData,
  onLocationButtonClick,
  cmsData,
  enquiryFormRef
}) => {
  const title = headingData?.title;
  const breadcrumbText = headingData?.breadcrumbText;
  const videoUrl = headingData?.videoUrl;
  const thumbnailUrl = headingData?.thumbnailUrl;
  const metricsData = headingData?.metricsData;

  const userPrevLoc = JSON.parse(localStorage.getItem('userLocation')) || {};
  const [isOpen, setIsOpen] = useState(false);
  const [isEnquiryFormDialogOpen, setIsEnquiryFormDialogOpen] = useState(false);
  const { isTablet, isMobile } = useWindowSize();

  function getYouTubeVideoId(url) {
    const regExp =
      /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[7]?.length === 11 ? match[7] : null;
  }

  const openPlayer = () => {
    setIsOpen(true);
  };

  const closePlayer = () => {
    setIsOpen(false);
  };

  const openEnquiryFormDialog = () => {
    if (isMobile || isTablet) {
      enquiryFormRef.current.scrollIntoView({ behavior: 'smooth' });
    } else {
      setIsEnquiryFormDialogOpen(true);
    }
  };

  const closeEnquiryFormDialog = () => {
    setIsEnquiryFormDialogOpen(false);
  };

  const handleAllCentreRouting = () => {
    pushRoute({
      pathname: '/all-centre'
    });
  };

  return (
    <Container>
      <Wrapper>
        <LeftContainer>
          <TitleContainer>
            {goalBreadcrumbData && (
              <GoalBreadcrumb
                baseOnClickHandler={handleAllCentreRouting}
                breadcrumbData={goalBreadcrumbData}
              />
            )}
            {breadcrumbText !== '' && (
              <BreadcrumbContainer>
                <BreadcrumbBgWrapper>
                  <Image
                    src="allCentre/bcTextBg.svg"
                    layout="fill"
                    objectFit="fill"
                    alt="Location Icon"
                  />
                </BreadcrumbBgWrapper>
                <BreadcrumbTextContainer>
                  <Image
                    src="allCentre/BreadCrumbIcon.svg"
                    width={16}
                    height={16}
                    alt="Breadcrumb Text Icon"
                  />
                  <BreadcrumbTextWrapper>
                    {breadcrumbText}
                  </BreadcrumbTextWrapper>
                </BreadcrumbTextContainer>
              </BreadcrumbContainer>
            )}
            <Typography variant="h1">{title}</Typography>
          </TitleContainer>
          <MetricsContainer>
            {metricsData.map((metrics, index) => {
              return (
                <HeadingMetrics
                  key={`metrics_${index}`}
                  text={metrics?.text}
                  subtext={metrics?.subtext}
                />
              );
            })}
          </MetricsContainer>
          <ButtonsContainer>
            {(!userPrevLoc?.lat || !userPrevLoc?.long) &&
              onLocationButtonClick && (
                <LocationButtonContainer onClick={onLocationButtonClick}>
                  <Image
                    src="allCentre/allCentreLocationIconWhite.svg"
                    width={18}
                    height={18}
                    alt="Location Icon"
                  />
                  <LocationButtonTextWrapper>
                    Find a centre near you
                  </LocationButtonTextWrapper>
                </LocationButtonContainer>
              )}
            <FormButtonContainer onClick={openEnquiryFormDialog}>
              <Image
                src="allCentre/BookACallCta.svg"
                width={56}
                height={48}
                alt="Location Icon"
              />
              <FormButtonTextWrapper>
                Book a call with us for any query
              </FormButtonTextWrapper>
            </FormButtonContainer>
          </ButtonsContainer>
          <EnquiryFormStyledDialog
            open={isEnquiryFormDialogOpen}
            onClose={closeEnquiryFormDialog}
          >
            <AllCentreDialogEnquiry
              closeDialogHandler={closeEnquiryFormDialog}
              cmsData={cmsData}
            />
          </EnquiryFormStyledDialog>
        </LeftContainer>
        <RightContainer>
          <VideoCardContainer>
            <StyledImg
              src={thumbnailUrl}
              alt="thumbnail"
              layout="fill"
              objectFit="cover"
            />

            <PlayBtn
              src="/static/images/subscriptionFeatures/playVideoBtn.png"
              width={146}
              height={40}
              alt="play"
              onClick={openPlayer}
            />
            <StyledDialog open={isOpen} onClose={closePlayer}>
              <YouTube
                opts={opts}
                videoId={getYouTubeVideoId(videoUrl)}
                onEnd={closePlayer}
                useWrapper={false}
              />
            </StyledDialog>
          </VideoCardContainer>
        </RightContainer>
      </Wrapper>
    </Container>
  );
};

export default AllCentreHeading;
